import {
  ATTENTION_STATE_LABELS,
  TEMPERATURE_LABELS,
  type AttentionTemperature,
  type LeadAttention,
  type LeadListItem,
  type PipelineStage,
  type ContactState,
  CONTACT_STATE_LABELS,
} from '@performance-crm/shared';
import { Bot, Flame, PauseCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { followupText, followupTone, STAGE_LABEL, STAGE_TONE } from '@/lib/format';

export function StageBadge({ stage }: { stage: PipelineStage }) {
  return <Badge tone={STAGE_TONE[stage]}>{STAGE_LABEL[stage]}</Badge>;
}

export function FollowupBadge({
  state,
  band,
  days,
}: {
  state: string;
  band: string | null;
  days: number;
}) {
  const tone = followupTone(state, band);
  const dot = {
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    danger: 'bg-red-500',
    muted: 'bg-slate-400',
  }[tone];
  return (
    <Badge tone={tone}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {followupText(state, band, days)}
    </Badge>
  );
}

export function LeadFollowupBadge({
  lead,
}: {
  lead: Pick<LeadListItem, 'followupState' | 'overdueBand' | 'overdueDays'>;
}) {
  return (
    <FollowupBadge state={lead.followupState} band={lead.overdueBand} days={lead.overdueDays} />
  );
}

export function ScoreBadge({ score }: { score: number }) {
  const tone = score >= 70 ? 'success' : score >= 40 ? 'warning' : 'muted';
  return (
    <Badge tone={tone} title="Puntaje del lead (0–100)">
      {score}
    </Badge>
  );
}

export function BotBadge() {
  return (
    <Badge tone="primary" title="Registrado por el bot">
      <Bot className="h-3 w-3" /> Bot
    </Badge>
  );
}

/** Temperatura del lead (según el puntaje): caliente, templado o frío. */
export function TemperatureBadge({ temperature }: { temperature: AttentionTemperature }) {
  const tone =
    temperature === 'CALIENTE' ? 'danger' : temperature === 'TEMPLADO' ? 'warning' : 'muted';
  return (
    <Badge tone={tone} title="Temperatura según el puntaje del lead">
      {temperature === 'CALIENTE' && <Flame className="h-3 w-3" />}
      {TEMPERATURE_LABELS[temperature]}
    </Badge>
  );
}

/** Estado de atención del bot: quién atiende (bot o asesor con el bot en pausa) y en qué estado está la conversación. */
export function AttentionBadge({
  attention,
  testId = 'attention-badge',
}: {
  attention: LeadAttention;
  testId?: string;
}) {
  const paused = attention.owner === 'HUMAN';
  return (
    <Badge
      tone={paused ? 'muted' : 'primary'}
      title={paused ? 'Un asesor está atendiendo; el bot está en pausa' : 'El bot está atendiendo'}
      data-testid={testId}
    >
      {paused ? <PauseCircle className="h-3 w-3" /> : <Bot className="h-3 w-3" />}
      {paused ? 'Bot en pausa' : 'Bot'} · {ATTENTION_STATE_LABELS[attention.state]}
    </Badge>
  );
}

/** 7.26: contacto del lead (sin contacto / no efectivo con intentos y motivo / efectivo). */
export function ContactBadge({
  state,
  attempts,
  reason,
  testId = 'contact-badge',
}: {
  state: ContactState;
  attempts: number;
  reason?: string | null;
  testId?: string;
}) {
  const tone = state === 'EFECTIVO' ? 'success' : state === 'NO_EFECTIVO' ? 'warning' : 'muted';
  const detail =
    state === 'NO_EFECTIVO'
      ? ` · ${attempts} intento${attempts === 1 ? '' : 's'}${reason ? ` · ${reason}` : ''}`
      : '';
  return (
    <Badge
      tone={tone}
      data-testid={testId}
      title="Contacto efectivo: alguna gestión con resultado efectivo"
    >
      {CONTACT_STATE_LABELS[state]}
      {detail}
    </Badge>
  );
}
